import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
  Alert,
  Box,
  IconButton,
  CircularProgress,
  Collapse,
  Fade
} from '@mui/material';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import DatePickerWrapper from '../DatePicker/DatePickerWrapper';
import { ko } from 'date-fns/locale';
import SearchIcon from '@mui/icons-material/Search'; 
import SyncAgGrid from '../common/DataGrid/SyncAgGrid';
import { axiosPrivate } from "../../api/axios";
import { useTranslation } from "react-i18next";

const MasterDataDialog = ({ 
  open, 
  onClose, 
  onSave, 
  data, 
  mode, 
  type,
  machineGroups = [],
  machineCategories = [],
  workerTeams = [],
  workerPositions = [],
  group_code,
  project_code,
  usedDtSyncIds = []
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [alertMessage, setAlertMessage] = useState('');
  
  // 동기화 패널 관련 상태 추가
  const [showSyncPanel, setShowSyncPanel] = useState(false);
  const [dtMachines, setDtMachines] = useState([]);
  const [syncLoading, setSyncLoading] = useState(false);
  const syncGridRef = useRef(null);
  const [selectedDtMachine, setSelectedDtMachine] = useState(null);

  // 날짜를 YYYY-MM-DD 형식으로 변환하는 함수
  const formatDate = (date) => {
    if (!date) return '';
    
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  };

  // 다이얼로그가 열릴 때 데이터 초기화
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && data) {
        // 편집 모드일 때 기존 데이터로 초기화
        const initialData = {...data};
        
        // 날짜 형식 변환
        if (initialData.machine_purchase_date) {
          initialData.machine_purchase_date = new Date(initialData.machine_purchase_date);
        }
        // FK 선택값 null을 ''로 정규화(Select 경고 방지)
        if (initialData.machine_category == null) initialData.machine_category = '';
        if (initialData.machine_group == null) initialData.machine_group = '';
        if (initialData.position_id == null) initialData.position_id = '';
        if (initialData.team_id == null) initialData.team_id = '';
        
        setFormData(initialData);
      } else {
        // 추가 모드일 때 빈 데이터로 초기화
        let initialData = {};
        
        if (type === 'machines') {
          initialData = {
            dt_sync_id: '',
            machine_name: '',
            machine_manage_code: '',
            machine_category: machineCategories.length > 0 ? machineCategories[0].machine_category_id : '',
            machine_group: machineGroups.length > 0 ? machineGroups[0].machine_group_id : '',
            machine_manufacturer: '',
            machine_model_name: '',
            machine_purchase_date: new Date(),
            machine_size: '',
            machine_volt: ''
          };
        } else if (type === 'workers') {
          initialData = {
            worker_id: '',
            worker_name: '',
            position_id: workerPositions.length > 0 ? workerPositions[0].position_id : '',
            team_id: workerTeams.length > 0 ? workerTeams[0].team_id : ''
          };
        } else if (type === 'machine-groups') {
          initialData = {
            machine_group_name: ''
          };
        } else if (type === 'machine-categories') {
          initialData = {
            machine_category_name: ''
          };
        } else if (type === 'worker-teams') {
          initialData = {
            team_name: ''
          };
        } else if (type === 'worker-positions') {
          initialData = {
            position_index: 0,
            position_name: ''
          };
        }
        
        setFormData(initialData);
      }
      
      setErrors({});
      setAlertMessage('');
      setShowSyncPanel(false);
      setSelectedDtMachine(null);
    }
  }, [open, data, mode, type]);

  // 선택 옵션(그룹/분류/부서/직급) 목록이 늦게 도착했을 때 기본값 보정
  useEffect(() => {
    if (!open || mode !== 'add') return;
    // 장비: 분류/그룹 기본값 보정
    if (type === 'machines') {
      if ((formData.machine_category === '' || formData.machine_category === undefined) && machineCategories.length > 0) {
        setFormData(prev => ({ ...prev, machine_category: machineCategories[0].machine_category_id }));
      }
      if ((formData.machine_group === '' || formData.machine_group === undefined) && machineGroups.length > 0) {
        setFormData(prev => ({ ...prev, machine_group: machineGroups[0].machine_group_id }));
      }
    }
    // 작업자: 직급/부서 기본값 보정
    if (type === 'workers') {
      if ((formData.position_id === '' || formData.position_id === undefined) && workerPositions.length > 0) {
        setFormData(prev => ({ ...prev, position_id: workerPositions[0].position_id }));
      }
      if ((formData.team_id === '' || formData.team_id === undefined) && workerTeams.length > 0) {
        setFormData(prev => ({ ...prev, team_id: workerTeams[0].team_id }));
      }
    }
  }, [open, mode, type, machineGroups, machineCategories, workerTeams, workerPositions]);

  // 입력 필드 변경 핸들러
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // 에러 상태 초기화
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // 날짜 변경 핸들러
  const handleDateChange = (date, name) => {
    setFormData(prev => ({
      ...prev,
      [name]: date
    }));
    
    // 에러 상태 초기화
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // 유효성 검사
  const validateForm = () => {
    const newErrors = {};
    
    if (type === 'machines') {
      if (!formData.machine_name) {
        newErrors.machine_name = t('pages.master.mch.dialog.validations.machineNameRequired');
      }
    } else if (type === 'workers') {
      if (!formData.worker_name) {
        newErrors.worker_name = t('pages.master.worker.dialog.validations.workerNameRequired');
      }
    } else if (type === 'machine-groups') {
      if (!formData.machine_group_name) {
        newErrors.machine_group_name = t('pages.master.mch.dialog.validations.groupNameRequired');
      }
    } else if (type === 'machine-categories') {
      if (!formData.machine_category_name) {
        newErrors.machine_category_name = t('pages.master.mch.dialog.validations.categoryNameRequired');
      }
    } else if (type === 'worker-teams') {
      if (!formData.team_name) {
        newErrors.team_name = t('pages.master.worker.dialog.validations.teamNameRequired');
      }
    } else if (type === 'worker-positions') {
      if (!formData.position_name) {
        newErrors.position_name = t('pages.master.worker.dialog.validations.positionNameRequired');
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 저장 핸들러
  const handleSave = () => {
    if (validateForm()) {
      // 날짜 형식 변환
      const submitData = {...formData};
      
      if (submitData.machine_purchase_date) {
        submitData.machine_purchase_date = formatDate(submitData.machine_purchase_date);
      }
      // FK 컬럼: 빈 문자열은 NULL로 변환
      if (type === 'machines') {
        if (submitData.machine_category === '') submitData.machine_category = null;
        if (submitData.machine_group === '') submitData.machine_group = null;
      } else if (type === 'workers') {
        if (submitData.position_id === '') submitData.position_id = null;
        if (submitData.team_id === '') submitData.team_id = null;
      }
      
      // 추가 모드에서는 machine_id가 필요 없으므로 제거
      // 편집 모드에서는 machine_id를 유지
      if (mode === 'add' && type === 'machines') {
        // machine_id가 있다면 제거 (안전을 위해)
        delete submitData.machine_id;
      }
      
      onSave(submitData, mode);
    } else {
      setAlertMessage(t('pages.master.mch.dialog.validations.fillRequiredFields'));
    }
  };

  // 다이얼로그 제목 가져오기
  const getDialogTitle = () => {
    if (type === 'workers' || type === 'worker-teams' || type === 'worker-positions') {
      return t(`pages.master.worker.dialog.title.${mode}_${type}`);
    }
    return t(`pages.master.mch.dialog.title.${mode}_${type}`);
  };

  // 디지털 트윈 장비 목록 로드 함수 수정
  const loadDtMachines = async () => {
    if (!group_code || !project_code) return;
    
    setSyncLoading(true);
    try {
      const response = await axiosPrivate.post('/master/machines/dt-list', {
        group_code,
        project_code
      });
      
      const machinesWithId = response.data.map((machine, index) => ({
        ...machine,
        id: machine.mchId || `row-${index}`,
        isUsed: usedDtSyncIds.includes(machine.mchId)
      }));
      
      setDtMachines(machinesWithId);
      
      // 편집 모드에서 현재 dt_sync_id와 일치하는 항목 선택
      if (formData.dt_sync_id) {
        const matchedMachine = machinesWithId.find(m => m.mchId === formData.dt_sync_id);
        if (matchedMachine) {
          setSelectedDtMachine(matchedMachine);
          
          // 그리드가 로드된 후 선택 상태 설정
          setTimeout(() => {
            if (syncGridRef.current && syncGridRef.current.api) {
              // 모든 행을 순회하며 일치하는 항목 찾기
              syncGridRef.current.api.forEachNode(node => {
                if (node.data.mchId === formData.dt_sync_id) {
                  node.setSelected(true);
                }
              });
            }
          }, 300);
        }
      }
    } catch (err) {
      console.error("디지털 트윈 장비 목록 조회 실패:", err);
      setAlertMessage(t('pages.master.mch.dialog.messages.dtLoadError'));
    } finally {
      setSyncLoading(false);
    }
  };

  // dt_sync_id 필드 클릭 핸들러 수정
  const handleDtSyncIdClick = () => {
    setShowSyncPanel(!showSyncPanel);
    if (!showSyncPanel) {
      loadDtMachines();
    }
  };

  // 그리드 준비 완료 핸들러 추가
  const handleSyncGridReady = (params) => {
    // 그리드가 준비되면 현재 dt_sync_id와 일치하는 항목 선택
    if (formData.dt_sync_id && dtMachines.length > 0) {
      setTimeout(() => {
        params.api.forEachNode(node => {
          if (node.data.mchId === formData.dt_sync_id) {
            node.setSelected(true);
            setSelectedDtMachine(node.data);
          }
        });
      }, 100);
    }
  };

  // 디지털 트윈 장비 선택 핸들러
  const handleDtMachineSelect = (params) => {
    setSelectedDtMachine(params.data);
    setFormData(prev => ({
      ...prev,
      dt_sync_id: params.data.mchId,
      machine_name: params.data.mesMchId || prev.machine_name
    }));
  };

  // 선택 적용 핸들러 수정
  const handleApplySelection = () => {
    if (selectedDtMachine) {
      setFormData(prev => ({
        ...prev,
        dt_sync_id: selectedDtMachine.mchId,
        machine_name: selectedDtMachine.mesMchId || prev.machine_name
      }));
    }
    // setShowSyncPanel(false);
  };

  // 동기화 패널 닫기 핸들러
  const handleCloseSyncPanel = () => {
    setShowSyncPanel(false);
  };

  // 동기화 그리드 선택 변경 핸들러
  const handleSyncSelectionChanged = () => {
    if (syncGridRef.current && syncGridRef.current.api) {
      const selectedNodes = syncGridRef.current.api.getSelectedNodes();
      if (selectedNodes.length > 0) {
        const selectedData = selectedNodes[0].data;
        setSelectedDtMachine(selectedData);
      } else {
        setSelectedDtMachine(null);
      }
    }
  };

  // 컬럼 정의 수정
  const syncColumnDefs = [
    { field: 'mchId', headerName: t('pages.master.mch.dialog.syncGrid.columns.machineId'), flex: 1 },
    { field: 'mesMchId', headerName: t('pages.master.mch.dialog.syncGrid.columns.machineName'), flex: 2 },
    { 
      field: 'isUsed', 
      headerName: t('pages.master.mch.dialog.syncGrid.columns.status'), 
      flex: 1,
      cellRenderer: params => {
        if (params.data.mchId === formData.dt_sync_id) {
          return t('pages.master.mch.dialog.syncGrid.status.current');
        }
        return params.value ? t('pages.master.mch.dialog.syncGrid.status.inUse') : t('pages.master.mch.dialog.syncGrid.status.available');
      },
      cellStyle: params => {
        if (params.data.mchId === formData.dt_sync_id) {
          return { color: 'blue', fontWeight: 'bold' };
        }
        return params.value ? { color: 'red' } : { color: 'green' };
      }
    }
  ];

  // 장비 폼 렌더링
  const renderMachineForm = () => (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            name="dt_sync_id"
            label={t('pages.master.mch.dialog.form.dtSyncId')}
            fullWidth
            value={formData.dt_sync_id || ''}
            onChange={handleChange}
            error={!!errors.dt_sync_id}
            helperText={errors.dt_sync_id}
            InputProps={{
              endAdornment: (
                <IconButton 
                  size="small" 
                  onClick={handleDtSyncIdClick}
                >
                  <SearchIcon />
                </IconButton>
              ),
              readOnly: false
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            name="machine_name"
            label={t('pages.master.mch.dialog.form.machineName')}
            fullWidth
            value={formData.machine_name || ''}
            onChange={handleChange}
            error={!!errors.machine_name}
            helperText={errors.machine_name}
            required
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            name="machine_manage_code"
            label={t('pages.master.mch.dialog.form.manageCode')}
            fullWidth
            value={formData.machine_manage_code || ''}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            name="machine_manufacturer"
            label={t('pages.master.mch.dialog.form.manufacturer')}
            fullWidth
            value={formData.machine_manufacturer || ''}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel id="machine-category-label">{t('pages.master.mch.dialog.form.machineCategory')}</InputLabel>
            <Select
              labelId="machine-category-label"
              name="machine_category"
              value={formData.machine_category ?? ''}
              onChange={handleChange}
              label={t('pages.master.mch.dialog.form.machineCategory')}
            >
              {machineCategories.length === 0 ? (
                <MenuItem value="">생성된 장비 분류가 없습니다.</MenuItem>
              ) : (
                machineCategories.map(category => (
                  <MenuItem key={category.machine_category_id} value={category.machine_category_id}>
                    {category.machine_category_name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel id="machine-group-label">{t('pages.master.mch.dialog.form.machineGroup')}</InputLabel>
            <Select
              labelId="machine-group-label"
              name="machine_group"
              value={formData.machine_group ?? ''}
              onChange={handleChange}
              label={t('pages.master.mch.dialog.form.machineGroup')}
            >
              {machineGroups.length === 0 ? (
                <MenuItem value="">생성된 장비 그룹이 없습니다.</MenuItem>
              ) : (
                machineGroups.map(group => (
                  <MenuItem key={group.machine_group_id} value={group.machine_group_id}>
                    {group.machine_group_name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            name="machine_model_name"
            label={t('pages.master.mch.dialog.form.modelName')}
            fullWidth
            value={formData.machine_model_name || ''}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DatePickerWrapper>
            <DatePicker
              selected={formData.machine_purchase_date}
              onChange={(date) => handleDateChange(date, 'machine_purchase_date')}
              dateFormat="yyyy-MM-dd"
              locale={ko}
              customInput={
                <TextField 
                  fullWidth 
                  label={t('pages.master.mch.dialog.form.purchaseDate')} 
                  error={!!errors.machine_purchase_date}
                  helperText={errors.machine_purchase_date}
                />
              }
            />
          </DatePickerWrapper>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            name="machine_size"
            label={t('pages.master.mch.dialog.form.size')}
            fullWidth
            value={formData.machine_size || ''}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            name="machine_volt"
            label={t('pages.master.mch.dialog.form.volt')}
            fullWidth
            value={formData.machine_volt || ''}
            onChange={handleChange}
          />
        </Grid>
      </Grid>
      
      {/* 동기화 패널 - Grid 컴포넌트 밖으로 이동 */}
      <Collapse in={showSyncPanel} timeout={300} sx={{ mt: 2 }}>
        <Fade in={showSyncPanel} timeout={400}>
          <Box sx={{ 
            mt: 2,
            p: 2, 
            border: '1px solid #e0e0e0', 
            borderRadius: 1,
            bgcolor: 'background.paper',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 2
            }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {t('pages.master.mch.dialog.syncPanel.title')}
              </Typography>
              <Button 
                color="error"
                variant="outlined" 
                size="small" 
                onClick={handleCloseSyncPanel}
              >
                {t('pages.master.mch.dialog.buttons.close')}
              </Button>
            </Box>
            
            <Box sx={{ height: 300, width: '100%' }}>
              {syncLoading ? (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  height: '100%' 
                }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    {t('pages.master.mch.dialog.messages.loading')}
                  </Typography>
                </Box>
              ) : (
                <SyncAgGrid
                  gridRef={syncGridRef}
                  rowData={dtMachines}
                  columnDefs={syncColumnDefs}
                  onRowDoubleClicked={handleDtMachineSelect}
                  onSelectionChanged={handleSyncSelectionChanged}
                  onGridReady={handleSyncGridReady}
                  pageSize={100}
                  isRowSelectable={(params) => !params.data.isUsed || params.data.mchId === formData.dt_sync_id}
                />
              )}
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleApplySelection}
                disabled={!selectedDtMachine}
              >
                {t('pages.master.mch.dialog.buttons.applySelection')}
              </Button>
            </Box>
          </Box>
        </Fade>
      </Collapse>
    </>
  );

  // 작업자 폼 렌더링
  const renderWorkerForm = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          name="worker_name"
          label={t('pages.master.worker.dialog.form.workerName')}
          fullWidth
          value={formData.worker_name || ''}
          onChange={handleChange}
          error={!!errors.worker_name}
          helperText={errors.worker_name}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel id="worker-team-label">{t('pages.master.worker.dialog.form.team')}</InputLabel>
          <Select
            labelId="worker-team-label"
            name="team_id"
            value={formData.team_id ?? ''}
            onChange={handleChange}
            label={t('pages.master.worker.dialog.form.team')}
          >
            {workerTeams.length === 0 ? (
              <MenuItem value="">{t('pages.master.worker.dialog.form.noTeams', '생성된 작업자 부서가 없습니다.')}</MenuItem>
            ) : (
              workerTeams.map(team => (
                <MenuItem key={team.team_id} value={team.team_id}>
                  {team.team_name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel id="worker-position-label">{t('pages.master.worker.dialog.form.position')}</InputLabel>
          <Select
            labelId="worker-position-label"
            name="position_id"
            value={formData.position_id ?? ''}
            onChange={handleChange}
            label={t('pages.master.worker.dialog.form.position')}
          >
            {workerPositions.length === 0 ? (
              <MenuItem value="">{t('pages.master.worker.dialog.form.noPositions', '생성된 작업자 직급이 없습니다.')}</MenuItem>
            ) : (
              workerPositions.map(pos => (
                <MenuItem key={pos.position_id} value={pos.position_id}>
                  {pos.position_name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );

  const renderWorkerTeamForm = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          name="team_name"
          label={t('pages.master.worker.dialog.form.teamName')}
          fullWidth
          value={formData.team_name || ''}
          onChange={handleChange}
          error={!!errors.team_name}
          helperText={errors.team_name}
          required
        />
      </Grid>
    </Grid>
  );

  const renderWorkerPositionForm = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          name="position_name"
          label={t('pages.master.worker.dialog.form.positionName')}
          fullWidth
          value={formData.position_name || ''}
          onChange={handleChange}
          error={!!errors.position_name}
          helperText={errors.position_name}
          required
        />
      </Grid>
    </Grid>
  );

  // 장비 그룹 폼 렌더링
  const renderMachineGroupForm = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          name="machine_group_name"
          label={t('pages.master.mch.dialog.form.groupName')}
          fullWidth
          value={formData.machine_group_name || ''}
          onChange={handleChange}
          error={!!errors.machine_group_name}
          helperText={errors.machine_group_name}
          required
        />
      </Grid>
    </Grid>
  );

  // 장비 분류 폼 렌더링
  const renderMachineCategoryForm = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          name="machine_category_name"
          label={t('pages.master.mch.dialog.form.categoryName')}
          fullWidth
          value={formData.machine_category_name || ''}
          onChange={handleChange}
          error={!!errors.machine_category_name}
          helperText={errors.machine_category_name}
          required
        />
      </Grid>
    </Grid>
  );

  // 타입에 따른 폼 렌더링
  const renderForm = () => {
    switch (type) {
      case 'machines':
        return renderMachineForm();
      case 'workers':
        return renderWorkerForm();
      case 'worker-teams':
        return renderWorkerTeamForm();
      case 'worker-positions':
        return renderWorkerPositionForm();
      case 'machine-groups':
        return renderMachineGroupForm();
      case 'machine-categories':
        return renderMachineCategoryForm();
      default:
        return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
        {getDialogTitle()}
      </DialogTitle>
      
      <Divider />
      
      <DialogContent sx={{ pt: 3 }}>
        {alertMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {alertMessage}
          </Alert>
        )}
        
        {renderForm()}
      </DialogContent>
      
      <Divider />
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleSave} variant="contained" color="primary">
          {t('pages.master.mch.dialog.buttons.save')}
        </Button>
        <Button onClick={onClose} color="inherit">
          {t('pages.master.mch.dialog.buttons.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MasterDataDialog; 